import { useCallback, useEffect, useState } from 'react';
import { useMedplum } from '@medplum/react';
import {
  QuestionnaireResponse,
  Patient,
  Questionnaire,
} from '@medplum/fhirtypes';
import { getReferenceString } from '@medplum/core';
import { PERINATAL_PERIOD_QUESTIONNAIRE_NAME } from '@/consts/questionnaires.consts';

interface UsePerinatalPeriodResponseResult {
  questionnaire: Questionnaire | undefined;
  questionnaireResponse: QuestionnaireResponse | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  deleteResponse: () => Promise<void>;
}

export const usePerinatalPeriodResponse = (
  patient: Patient | undefined
): UsePerinatalPeriodResponseResult => {
  const medplum = useMedplum();
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | undefined>(
    undefined
  );
  const [questionnaireResponse, setQuestionnaireResponse] =
    useState<QuestionnaireResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!patient?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const questionnaire = await medplum.searchOne('Questionnaire', {
        name: PERINATAL_PERIOD_QUESTIONNAIRE_NAME,
      });

      if (!questionnaire) {
        setQuestionnaire(undefined);
        setQuestionnaireResponse(null);
        setLoading(false);
        return;
      }

      setQuestionnaire(questionnaire);
      const response = await medplum.searchOne('QuestionnaireResponse', {
        questionnaire: getReferenceString(questionnaire),
        subject: getReferenceString(patient),
        _sort: '-authored',
      });

      setQuestionnaireResponse(response || null);
    } catch (err) {
      console.error('Error fetching perinatal period response:', err);
      setError(
        err instanceof Error
          ? err
          : new Error('Failed to fetch perinatal period response')
      );
    } finally {
      setLoading(false);
    }
  }, [medplum, patient]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Delete the questionnaire response
  const deleteResponse = useCallback(async () => {
    if (!questionnaireResponse?.id) {
      return;
    }

    try {
      await medplum.deleteResource('QuestionnaireResponse', questionnaireResponse.id);
      // Clear the response from state
      setQuestionnaireResponse(null);
      // Optionally refetch to ensure consistency
      await fetchData();
    } catch (err) {
      console.error('Error deleting perinatal period response:', err);
      throw err;
    }
  }, [medplum, questionnaireResponse, fetchData]);

  return {
    questionnaire,
    questionnaireResponse,
    loading,
    error,
    refetch: fetchData,
    deleteResponse,
  };
};
